// ForgotPassword.jsx - Forgot password component with AuthLayout
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FONT_SIZE, TOUCH } from '@/core/platform/platformStandards';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Email as EmailIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';
import AuthLayout from '@/features/auth/components/AuthLayout';
import HFRLogo from '@/components/common/HFRLogo';
import PasswordResetModal from '@/features/auth/components/PasswordResetModal';
import authService from '@/services/authService';
import { useNotification } from '@/core/contexts/NotificationContext';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { notifySuccess } = useNotification();
  const theme = useTheme();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const result = await authService.forgotPassword(email);
      
      if (result.success) {
        setSuccess(true);
        notifySuccess(result.message);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'Failed to send password reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(''); // Clear error when user types
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!confirmationCode) {
      setError('Confirmation code is required');
      return;
    }

    if (confirmationCode.length < 6) {
      setError('Confirmation code must be at least 6 characters');
      return;
    }

    // Open password modal
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (newPassword) => {
    setLoading(true);
    setError('');

    try {
      const result = await authService.resetPassword(email, confirmationCode, newPassword);
      
      if (result.success) {
        setShowPasswordModal(false);
        notifySuccess(result.message);
        navigate('/login');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setSuccess(false);
    setEmail('');
    setConfirmationCode('');
    setError('');
  };

  return (
    <AuthLayout>
      <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
        {/* HFR Connect Logo */}
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            mb: 4
          }}
        >
          <HFRLogo width={140} height={70} />
        </Box>

        {!success ? (
          <>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Reset Your Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your email address and we&apos;ll send you a code to reset your password
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                fullWidth
                margin="dense"
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon
                        // intentional: adornment icon sized above theme default for visual balance with input text
                        sx={{ color: theme.palette.text.secondary, fontSize: FONT_SIZE.xl }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    minHeight: TOUCH.minSize,
                    fontSize: FONT_SIZE.sm,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 3,
                  minHeight: { xs: TOUCH.minSize, sm: TOUCH.comfySize },
                  fontSize: FONT_SIZE.sm,
                }}
              >
                {loading ? (
                  <CircularProgress size={TOUCH.iconSize} color="inherit" />
                ) : (
                  'Send Reset Code'
                )}
              </Button>
            </form>

            <Divider sx={{ my: 2 }} />

            {/* Back to Login */}
            <Button
              onClick={handleBackToLogin}
              fullWidth
              variant="outlined"
              startIcon={<BackIcon />}
            >
              Back to Login
            </Button>
          </>
        ) : (
          <>
            {/* Streamlined Code Entry */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Check Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the 6-digit code from your email below
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Code Entry Form */}
            <form onSubmit={handleCodeSubmit}>
              <TextField
                label="Confirmation Code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                fullWidth
                margin="dense"
                autoComplete="one-time-code"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon
                        // intentional: adornment icon sized above theme default for visual balance with input text
                        sx={{ color: theme.palette.text.secondary, fontSize: FONT_SIZE.xl }}
                      />
                    </InputAdornment>
                  ),
                }}
                helperText="Enter the 6-digit code sent to your email"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    minHeight: TOUCH.minSize,
                    fontSize: FONT_SIZE.sm,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 3,
                  minHeight: { xs: TOUCH.minSize, sm: TOUCH.comfySize },
                  fontSize: FONT_SIZE.sm,
                }}
              >
                {loading ? (
                  <CircularProgress size={TOUCH.iconSize} color="inherit" />
                ) : (
                  'Continue to Set Password'
                )}
              </Button>
            </form>

            <Divider sx={{ my: 2 }} />

            {/* Navigation Buttons */}
            <Button
              onClick={handleTryAgain}
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
            >
              Try Different Email
            </Button>

            <Button
              onClick={handleBackToLogin}
              fullWidth
              variant="text"
              startIcon={<BackIcon />}
            >
              Back to Login
            </Button>
          </>
        )}
      </Box>

      {/* Password Reset Modal */}
      <PasswordResetModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordSubmit}
        loading={loading}
        error={error}
        title="Set New Password"
        submitButtonText="Reset Password"
      />
    </AuthLayout>
  );
};

export default ForgotPassword;
